import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UtilService } from '../../shared/services/util.service';
import { HouseViewsService } from '../../shared/services/house-views.service';
import { PortfolioService } from '../../pages/portfolio/portfolio.service';
import { FilterSelectComponent } from '../filter-select/filter-select.component';
import { MaterialModule } from 'src/app/app.materials';
import { FormsModule } from '@angular/forms';
import { CleanNamePipe } from '../../shared/services/clean-name.pipe';
import { BenchmarkSelectorForViewsComponent } from '../benchmark-selector-for-views/benchmark-selector-for-views.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-house-views-setter',
  templateUrl: './house-views-setter.component.html',
  styleUrls: ['./house-views-setter.component.scss'],
  imports: [FilterSelectComponent, MaterialModule, FormsModule, CleanNamePipe, BenchmarkSelectorForViewsComponent, CommonModule],
  standalone: true,
})
export class HouseViewsSetterComponent implements OnInit {

  @Input() mode: 'user' | 'firm' = 'user';
  @Input() views: any[] = [];

  activeView: any = {};
  list: any[] = [];

  assets: any[] = [];
  sectors: any[] = [];
  countries: any[] = [];

  types = [
    { id: 'asset', name: 'Asset' },
    { id: 'sector', name: 'Sector' },
    { id: 'country', name: 'Country' },
    { id: 'index', name: 'Market Factors' },
    { id: 'currency', name: 'Currency' },
  ];

  viewTypes = [
    { id: 'absolute', name: 'Absolute' },
    { id: 'relative', name: 'Relative' },
  ];

  frequencies = [
    { id: 'daily', name: 'Daily' },
    { id: 'weekly', name: 'Weekly' },
    { id: 'monthly', name: 'Monthly' },
    { id: 'quarterly', name: 'Quarterly' },
  ];

  constructor(
    private util: UtilService,
    private service: HouseViewsService,
    private portfolioService: PortfolioService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.fetchFilterData();
  }

  fetchFilterData() {
    this.portfolioService.fetchData({}, {}).subscribe((resp: any) => {
      if (resp && resp.sectors) {
        this.sectors = resp.sectors;
      }
      if (resp && resp.countries) {
        this.countries = resp.countries;
      }
      if (resp && resp.data) {
        this.assets = resp.data.map((s: any) => {
          s.name = s.company_name || s.name;
          return s;
        });
        this.assets.sort((a, b) => (a.name?.toLowerCase() > b.name?.toLowerCase()) ? 1 : -1);
      }
    });
  }

  onTypeChange() {
    this.list = [];
    delete this.activeView.items;
    switch (this.activeView.type) {
      case 'asset':
        this.list = this.assets;
        break;
      case 'sector':
        this.list = this.sectors;
        break;
      case 'country':
        this.list = this.countries;
        break;
      default:
        this.list = [];
    }
  }

  onViewTypeChange() {
    if (this.activeView.view_type === 'absolute') {
      delete this.activeView.items2;
    }
  }

  initActiveView() {
    return {
      type: 'asset',
    };
  }

  onAddView() {
    this.activeView = this.initActiveView();
    this.list = this.assets;
  }

  saveView() {
    if (!this.activeView.type || !this.activeView.items) {
      return;
    }

    const payload = { ...this.activeView };

    // Convert percentage display to decimal for API
    if (payload.value != null) {
      payload.value = payload.value / 100;
    }

    // Convert benchmark to array with id for index/currency types
    if (payload.type === 'index' || payload.type === 'currency') {
      payload.items = [payload.items.id];
    }

    const createFn = this.mode === 'firm'
      ? this.service.createFirmView(payload)
      : this.service.createUserView(payload);

    createFn.subscribe((resp: any) => {
      if (resp.error) {
        return this.service.notificationService.open(resp.error);
      }
      if (this.activeView.id) {
        this.views = this.util.findAndReplaceItemInArray(resp, this.views);
      } else {
        this.views.push(resp);
      }
      this.discardView();
      this.cdr.detectChanges();
    });
  }

  editView(c: any) {
    this.activeView = { ...c };
    this.activeView.type = c.type.id;

    this.onTypeChange();

    if (this.activeView.type === 'index' || this.activeView.type === 'currency') {
      if (c.items && c.items.length > 0) {
        c.items = c.items.map((i: any) => {
          if (!i.name_full) {
            i.name_full = i.name;
          }
          return i;
        });
      }
      this.activeView.items = c.items[0];
    } else {
      this.activeView.items = c.items.map((i: any) => +i.id);
    }

    if (c.items2 && c.items2.length) {
      this.activeView.items2 = c.items2.map((i: any) => +i.id);
    }

    // Convert decimal to percentage for display
    if (this.activeView.value != null) {
      this.activeView.value = this.activeView.value * 100;
    }
  }

  deleteView(v: any) {
    const deleteFn = this.mode === 'firm'
      ? this.service.deleteFirmView(v)
      : this.service.deleteUserView(v);

    deleteFn.subscribe(() => {
      this.views = this.views.filter(view => view.id !== v.id);
      this.cdr.detectChanges();
    });
  }

  discardView() {
    this.activeView = {};
  }

  getTypeName(type: any): string {
    if (!type) return '';
    const id = type.id || type;
    const found = this.types.find(t => t.id === id);
    return found ? found.name : id;
  }
}
